/**
 * deliverableHelpers.ts
 *
 * Typed parsers for the two JSONB submission fields:
 *   milestone_submissions.content_urls
 *   milestone_submissions.review_notes
 *
 * v1 schema contracts
 * -------------------
 * content_urls:
 *   { deliverables: { [deliverableId]: DeliverableVideoEntry | DeliverableSameAsEntry } }
 *
 * review_notes:
 *   { deliverables: { [deliverableId]: DeliverableReviewNote } }
 *
 * All consumers should go through parseContentUrls / parseReviewNotes rather
 * than casting `as any` inline, so a future move from JSONB columns to a
 * dedicated table only requires changing these helpers.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeliverableVideoEntry {
  video: string
  caption: string | null
  metadata?: Record<string, unknown> | null
}

export interface DeliverableSameAsEntry {
  sameAsDeliverableId: string
}

export type ContentUrlsDeliverables = Record<
  string,
  DeliverableVideoEntry | DeliverableSameAsEntry
>

export interface DeliverableReviewNote {
  video?: { status: 'approved' | 'revision_requested'; comment?: string | null }
  caption?: { status: 'approved' | 'revision_requested'; comment?: string | null }
}

export type ReviewNotesDeliverables = Record<string, DeliverableReviewNote>

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

/** Parse the raw content_urls value from a submission into the typed deliverables map. */
export function parseContentUrls(raw: unknown): ContentUrlsDeliverables | null {
  if (!raw || typeof raw !== 'object') return null
  const deliverables = (raw as Record<string, unknown>).deliverables
  if (!deliverables || typeof deliverables !== 'object') return null
  return deliverables as ContentUrlsDeliverables
}

/** Parse the raw review_notes value from a submission into the typed deliverables map. */
export function parseReviewNotes(raw: unknown): ReviewNotesDeliverables | null {
  if (!raw || typeof raw !== 'object') return null
  const deliverables = (raw as Record<string, unknown>).deliverables
  if (!deliverables || typeof deliverables !== 'object') return null
  return deliverables as ReviewNotesDeliverables
}

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

/** Return the video URL for a specific deliverable, or null if absent / same-as entry. */
export function getDeliverableVideoUrl(
  contentUrls: ContentUrlsDeliverables | null,
  deliverableId: string,
): string | null {
  if (!contentUrls) return null
  const entry = contentUrls[deliverableId]
  if (!entry || 'sameAsDeliverableId' in entry) return null
  return (entry as DeliverableVideoEntry).video ?? null
}

/** Return the review note for a specific deliverable. */
export function getDeliverableReviewNote(
  reviewNotes: ReviewNotesDeliverables | null,
  deliverableId: string,
): DeliverableReviewNote | null {
  if (!reviewNotes) return null
  return reviewNotes[deliverableId] ?? null
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

/** True when every video/caption status in the review notes map is 'approved'. */
export function isAllApproved(reviewNotes: ReviewNotesDeliverables): boolean {
  const statuses = Object.values(reviewNotes).flatMap((dr) =>
    [dr?.video?.status, dr?.caption?.status].filter(Boolean),
  )
  return statuses.length > 0 && statuses.every((s) => s === 'approved')
}

/** True when any video/caption status in the review notes map is 'revision_requested'. */
export function hasRevisionRequested(reviewNotes: ReviewNotesDeliverables): boolean {
  return Object.values(reviewNotes).some(
    (dr) =>
      dr?.video?.status === 'revision_requested' || dr?.caption?.status === 'revision_requested',
  )
}
